import { useCallback, useState } from "react";
import { generatePath, useNavigate } from "react-router";

import { getTodaysDate } from "@/lib/dayjs/get-todays-date";
import type {
	HotelApplicationDataQueryQuery,
	ProTicketPurchaseInput,
	TicketListAvailable_BillingPageQuery,
} from "@/lib/gql/__generated__/graphql";
import { AccountTypeHotel, type BillingStatusUpdateInput } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { useHotelApplicationDataMutation } from "@/components/features/_shared/repository/hotel-application-data/mutation";
import { useTicketProPurchaseMutation } from "@/components/features/_shared/repository/ticket-pro-purchase/mutation";

export type OnUpdatePurchaseBasicBillingTicketType = ({
	defaultBasicBillingTicketID,
}: {
	defaultBasicBillingTicketID: string;
}) => Promise<boolean>;

export type OnUpdatePurchaseProBillingTicketType = ({
	defaultProBillingTicketID,
	isStopRecurringProBilling,
}: {
	defaultProBillingTicketID: string;
	isStopRecurringProBilling: boolean;
}) => Promise<boolean>;

export type OnUpdateRecurringPurchaseProBillingTicketType = ({
	defaultProBillingTicketID,
	isStopRecurringProBilling,
}: {
	defaultProBillingTicketID: string;
	isStopRecurringProBilling: boolean;
}) => Promise<boolean>;

export type BillingSettingDialogStateType = {
	BASIC: boolean;
	PRO: boolean;
	RECURRING_PRO: boolean;
};
export type BillingSettingDialogType = "BASIC" | "PRO" | "RECURRING_PRO";

type Props = {
	hotelID: string;
	activateCreditCardSettingDialog: () => void;
	hotelApplicationData: HotelApplicationDataQueryQuery;
	availableTicketList: TicketListAvailable_BillingPageQuery;
};

export const useInitialized = ({
	hotelID,
	activateCreditCardSettingDialog,
	hotelApplicationData,
	availableTicketList,
}: Props) => {
	const { billingStatus, hotelApplicationStatus } = hotelApplicationData;
	const { hotelAccountType, isCreditCardRegistered } = hotelApplicationStatus;
	const { validDtEndPro } = billingStatus;

	/**
	 * 無料チケットが存在するかどうか
	 */
	const onComputeFreeTicketExistsByTicketType = useCallback(
		(type: BillingSettingDialogType) => {
			switch (type) {
				case "BASIC":
					return availableTicketList.ticketBasicListAvailable.tickets.some(
						(ticket) => ticket?.price.amount === 0,
					);
				case "PRO":
					return availableTicketList.ticketProListAvailableSingleBilling.tickets.some(
						(ticket) => ticket?.price.amount === 0,
					);
				case "RECURRING_PRO":
					return availableTicketList.ticketProListAvailableContinuousBilling.tickets.some(
						(ticket) => ticket?.price.amount === 0,
					);
				default:
					return false;
			}
		},
		[availableTicketList],
	);

	const navigate = useNavigate();

	// モーダルの開閉状態
	const [openBillingSettingDialog, setOpenBillingSettingDialog] = useState<{
		BASIC: boolean;
		PRO: boolean;
		RECURRING_PRO: boolean;
	}>({
		BASIC: false,
		PRO: false,
		RECURRING_PRO: false,
	});
	const handleOpenChange = useCallback(
		(type: BillingSettingDialogType) => (callBackFunc: NodeJS.Timeout) => (open: boolean) => {
			if (open) {
				setOpenBillingSettingDialog((prev) => {
					return {
						...prev,
						[type]: open,
					};
				});
			} else {
				setOpenBillingSettingDialog((prev) => {
					return {
						...prev,
						[type]: open,
					};
				});
				callBackFunc;
			}
		},
		[],
	);
	const activateBillingSettingDialog = useCallback(
		(type: BillingSettingDialogType) => {
			if (isCreditCardRegistered || onComputeFreeTicketExistsByTicketType(type)) {
				// 無料のチケットはクレジットカード情報の登録がなくても購入できる
				setOpenBillingSettingDialog((prev) => {
					return {
						...prev,
						[type]: true,
					};
				});
			} else {
				activateCreditCardSettingDialog();
			}
		},
		[activateCreditCardSettingDialog, onComputeFreeTicketExistsByTicketType, isCreditCardRegistered],
	);
	const deactivateBillingSettingDialog = useCallback((type: BillingSettingDialogType) => {
		setOpenBillingSettingDialog((prev) => {
			return {
				...prev,
				[type]: false,
			};
		});
	}, []);

	/**
	 * pro版を使っているのかどうか
	 */
	const isUsingPro = (() => {
		if (hotelAccountType === AccountTypeHotel.Premium) {
			// プレミアムアカウントの場合（自社施設）
			return true;
		}

		if (validDtEndPro) {
			// Proチケットを購入している場合
			const todaysDate = getTodaysDate();

			return validDtEndPro >= todaysDate; // 有効期限内かどうかを判定
		}

		return false;
	})();

	const { ticketBasicListAvailable, ticketProListAvailableContinuousBilling, ticketProListAvailableSingleBilling } =
		availableTicketList;

	const { onUpdateHotelApplicationData } = useHotelApplicationDataMutation();
	const { onUpdateTicketProPurchase } = useTicketProPurchaseMutation();

	/**
	 * 基本料金のチケット購入を行う
	 */
	const onPurchaseBasicBillingTicket: OnUpdatePurchaseBasicBillingTicketType = useCallback(
		async ({ defaultBasicBillingTicketID }) => {
			// クレジットカードを登録していない場合は処理を中断
			// 無料のチケットはクレジットカード情報の登録がなくても購入できる
			if (!isCreditCardRegistered && !onComputeFreeTicketExistsByTicketType("BASIC")) {
				window.alert("クレジットカードが登録されていないため、購入できません");
				return false;
			}

			const input: BillingStatusUpdateInput = {
				defaultProBillingTicketID: billingStatus.defaultProBillingTicketID,
				hotelID,
				isStopRecurringBasicBilling: false,
				isStopRecurringProBilling: billingStatus.isStopRecurringProBilling,

				defaultBasicBillingTicketID,
			} satisfies BillingStatusUpdateInput;

			return await onUpdateHotelApplicationData(input);
		},
		[
			billingStatus.defaultProBillingTicketID,
			billingStatus.isStopRecurringProBilling,
			onComputeFreeTicketExistsByTicketType,
			hotelID,
			isCreditCardRegistered,
			onUpdateHotelApplicationData,
		],
	);

	/**
	 * Proチケットの購入を行う
	 */
	const onPurchaseProBillingTicket: OnUpdatePurchaseProBillingTicketType = useCallback(
		async ({ defaultProBillingTicketID, isStopRecurringProBilling }) => {
			// クレジットカードを登録していない場合は処理を中断
			// 無料のチケットはクレジットカード情報の登録がなくても購入できる
			if (!isCreditCardRegistered && !onComputeFreeTicketExistsByTicketType("PRO")) {
				window.alert("クレジットカードが登録されていないため、購入できません");
				return false;
			}

			const input: ProTicketPurchaseInput = {
				proTicketID: defaultProBillingTicketID,
				hotelID,
				isStopRecurringProBilling,
			} satisfies ProTicketPurchaseInput;

			return await onUpdateTicketProPurchase(input);
		},
		[
			hotelID, //
			isCreditCardRegistered,
			onComputeFreeTicketExistsByTicketType,
			onUpdateTicketProPurchase,
		],
	);

	/**
	 * Proチケットの定期購入を行う
	 */
	const onRecurringPurchaseProBillingTicket: OnUpdateRecurringPurchaseProBillingTicketType = useCallback(
		async ({ defaultProBillingTicketID, isStopRecurringProBilling }) => {
			// クレジットカードを登録していない場合は処理を中断
			// 無料のチケットはクレジットカード情報の登録がなくても購入できる
			if (!isCreditCardRegistered && !onComputeFreeTicketExistsByTicketType("RECURRING_PRO")) {
				window.alert("クレジットカードが登録されていないため、購入できません");
				return false;
			}

			const input: BillingStatusUpdateInput = {
				defaultBasicBillingTicketID: billingStatus.defaultBasicBillingTicketID,
				hotelID,
				isStopRecurringBasicBilling: billingStatus.isStopRecurringBasicBilling,

				defaultProBillingTicketID,
				isStopRecurringProBilling,
			} satisfies BillingStatusUpdateInput;

			return await onUpdateHotelApplicationData(input);
		},
		[
			billingStatus.defaultBasicBillingTicketID,
			billingStatus.isStopRecurringBasicBilling,
			hotelID,
			isCreditCardRegistered,
			onComputeFreeTicketExistsByTicketType,
			onUpdateHotelApplicationData,
		],
	);

	/**
	 * 解約ページへ遷移する
	 */
	const navigateToCancellationPage = useCallback(() => {
		navigate(generatePath(PATHS.CANCELLATION, { hotelID }));
	}, [hotelID, navigate]);

	return {
		ticketBasicListAvailable,
		ticketProListAvailableContinuousBilling,
		ticketProListAvailableSingleBilling,

		openBillingSettingDialog,
		handleOpenChange,
		activateBillingSettingDialog,
		deactivateBillingSettingDialog,

		/**
		 * pro版を使っているのかどうか
		 */
		isUsingPro,
		billingStatus,
		onPurchaseBasicBillingTicket,
		onPurchaseProBillingTicket,
		onRecurringPurchaseProBillingTicket,

		navigateToCancellationPage,
	};
};
